import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FormField, inputClass } from "@/components/Panel";
import { addDays, myTimeQuery, projectDetailQuery, projectListQuery, weekStart, type MyTimeEntry } from "@/features/project/api";
import { BookTimeForm } from "@/features/project/ProjectPanel";
import { useActiveCompany } from "@/lib/activeCompany";
import { cn } from "@/lib/cn";
import { todayIso, useFormat } from "@/lib/format";
import { useCan } from "@/lib/session";

// Project → My time: the caller's bookings for one ISO week (my_time_entries,
// project_time_entries_user_idx, ≤ 500 rows) and a booking form on active
// projects of the active company. Round-trips: 1 for the week, +1 project list
// and +1 project document when booking.

interface Search {
  week: string;
}

const ISO = /^\d{4}-\d{2}-\d{2}$/;

export const Route = createFileRoute("/_app/project/my-time")({
  validateSearch: (raw: Record<string, unknown>): Search => ({
    week: weekStart(typeof raw.week === "string" && ISO.test(raw.week) && !Number.isNaN(Date.parse(raw.week)) ? raw.week : todayIso()),
  }),
  loaderDeps: ({ search }) => ({ week: search.week }),
  loader: ({ context, deps }) => context.queryClient.ensureQueryData(myTimeQuery(deps.week, addDays(deps.week, 6))),
  component: MyTimePage,
});

function MyTimePage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const { week } = Route.useSearch();
  const navigate = Route.useNavigate();
  const canBook = useCan("project.time.book");
  const to = addDays(week, 6);
  const entries = useQuery(myTimeQuery(week, to));
  const days = Array.from({ length: 7 }, (_, i) => addDays(week, i));
  const byDay = new Map<string, MyTimeEntry[]>();
  for (const e of entries.data ?? []) byDay.set(e.work_date, [...(byDay.get(e.work_date) ?? []), e]);
  const total = (es: MyTimeEntry[]) => es.reduce((s, e) => s + Math.round(Number(e.hours) * 100), 0) / 100;
  const weekTotal = total(entries.data ?? []);
  const go = (w: string) => void navigate({ search: { week: w }, replace: true });

  return (
    <div className="flex min-h-0 grow flex-col gap-4">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col gap-0.5">
          <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
            {t("nav.project.title")} <span aria-hidden="true">/</span> {t("nav.project.myTime")}
          </nav>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("project.myTime.title")}</h1>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label={t("project.myTime.prev")}
            onClick={() => go(addDays(week, -7))}
            className="flex size-9 items-center justify-center rounded-xl border border-border bg-surface hover:bg-surface-hover"
          >
            <ChevronLeft size={16} aria-hidden="true" />
          </button>
          <span className="tabular min-w-[180px] text-center text-[13px] font-medium" data-testid="week-label">
            {fmt.day(week)} – {fmt.dayYear(to)}
          </span>
          <button
            type="button"
            aria-label={t("project.myTime.next")}
            onClick={() => go(addDays(week, 7))}
            className="flex size-9 items-center justify-center rounded-xl border border-border bg-surface hover:bg-surface-hover"
          >
            <ChevronRight size={16} aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => go(weekStart(todayIso()))}
            className="h-9 rounded-xl border border-border bg-surface px-3 text-[13px] font-medium hover:bg-surface-hover"
          >
            {t("project.myTime.thisWeek")}
          </button>
        </div>
      </header>

      <div className="grid min-h-0 grow gap-4 xl:grid-cols-[minmax(0,1.6fr)_minmax(320px,1fr)]">
        <section aria-label={t("project.myTime.week")} className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-border bg-surface">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h2 className="text-[15px] font-semibold">{t("project.myTime.week")}</h2>
            <span className="tabular text-[13px]" data-testid="week-total">
              {t("project.myTime.total", { hours: fmt.qty(String(weekTotal)) })}
            </span>
          </div>
          <div className="min-h-0 overflow-y-auto">
            {entries.isPending ? (
              <div className="flex flex-col gap-2 p-4" aria-hidden="true">
                {days.map((d) => (
                  <div key={d} className="h-9 animate-pulse rounded-lg bg-surface-muted" />
                ))}
              </div>
            ) : entries.isError ? (
              <p className="p-4 text-[13px] text-(--status-danger-fg)">{t("list.errorTitle")}</p>
            ) : (
              <table className="tabular w-full border-collapse text-[13px]">
                <tbody>
                  {days.map((d) => {
                    const es = byDay.get(d) ?? [];
                    return [
                      <tr key={d} className={cn("border-t border-border-subtle bg-surface-muted/50 first:border-t-0", d === todayIso() && "text-accent-ink")}>
                        <th scope="rowgroup" colSpan={2} className="px-4 py-2 text-left font-medium capitalize">
                          {fmt.longDay(new Date(`${d}T12:00:00`))}
                        </th>
                        <td className="px-4 py-2 text-right font-medium">{es.length ? fmt.qty(String(total(es))) : "—"}</td>
                      </tr>,
                      ...es.map((e) => (
                        <tr key={e.id} className="border-t border-border-subtle">
                          <td className="w-56 whitespace-nowrap px-4 py-1.5 font-mono text-xs">
                            {e.project_code} · {e.activity_code}
                          </td>
                          <td className="px-4 py-1.5">
                            <span className="block truncate">{e.activity_name}</span>
                            {e.note && <span className="block truncate text-xs text-ink-muted">{e.note}</span>}
                          </td>
                          <td className={cn("w-20 px-4 py-1.5 text-right", Number(e.hours) < 0 && "text-(--status-danger-fg)")}>{fmt.qty(String(e.hours))}</td>
                        </tr>
                      )),
                    ];
                  })}
                </tbody>
              </table>
            )}
          </div>
        </section>
        {canBook && <BookPanel />}
      </div>
    </div>
  );
}

function BookPanel() {
  const { t } = useTranslation();
  const { company } = useActiveCompany();
  const projects = useInfiniteQuery(projectListQuery({ companyId: company?.id ?? "", status: "active", q: "" }));
  const rows = projects.data?.pages.flatMap((p) => p.rows) ?? [];
  const [projectId, setProjectId] = useState("");
  const detail = useQuery({ ...projectDetailQuery(projectId), enabled: !!projectId });
  const activities = (detail.data?.activities ?? []).filter((a) => !a.is_done);
  return (
    <section aria-label={t("project.bookTime")} className="flex flex-col gap-3 self-start rounded-2xl border border-border bg-surface p-4">
      <h2 className="text-[15px] font-semibold">{t("project.bookTime")}</h2>
      <FormField id="mt-project" label={t("project.myTime.project")}>
        <select id="mt-project" value={projectId} onChange={(e) => setProjectId(e.target.value)} className={inputClass}>
          <option value="">—</option>
          {rows.map((p) => (
            <option key={p.id} value={p.id}>
              {p.code} — {p.name}
            </option>
          ))}
        </select>
      </FormField>
      {projects.isSuccess && rows.length === 0 && <p className="text-[13px] text-ink-muted">{t("project.myTime.noActive")}</p>}
      {projectId && detail.isSuccess && (activities.length > 0 ? (
        <BookTimeForm key={projectId} projectId={projectId} activities={activities} />
      ) : (
        <p className="text-[13px] text-ink-muted">{t("project.myTime.noOpenActivities")}</p>
      ))}
    </section>
  );
}
